import { Box, Card, CardContent, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { DocAttachment, DocDetail } from "@xop/dashboard-shared";

import { ConfirmDialog } from "../../components/ui/confirm-dialog.js";
import {
  bumpVersion,
  createDoc,
  deleteAttachment,
  listAttachments,
  listCategories,
  listReleases,
  listSolutionAreas,
  publishDoc,
  replaceFile,
  updateDoc,
} from "./api.js";
import { AttachmentPanel } from "./doc-form/AttachmentPanel.js";
import { composePayload, EMPTY, type FormState } from "./doc-form/formState.js";
import { DocAccessPanel } from "./doc-form/DocAccessPanel.js";
import { DocDetailsForm } from "./doc-form/DocDetailsForm.js";
import {
  EMPTY_FAQ,
  FaqFieldsEditor,
  parseFaqMarkdown,
  serializeFaqMarkdown,
  type FaqPair,
} from "./FaqFieldsEditor.js";
import { MarkdownEditor } from "./MarkdownEditor.js";
import {
  EMPTY_VIDEO_FIELDS,
  fromMetadata as videoFromMetadata,
  VideoFieldsEditor,
  type VideoFields,
} from "./VideoFieldsEditor.js";

/**
 * Shared create + edit form. When `existing` is provided we're editing —
 * the form starts pre-populated, sticky right-side panel shows attached
 * files for upload-mode docs (the file IS the content, no Markdown body),
 * and "Save" issues PATCH + a new version when the body has changed.
 *
 * When `existing` is undefined we're creating a manual-mode doc —
 * single POST with title, body, status, optional taxonomy.
 *
 * This file is the form orchestrator: the doc-type-aware state, the queries,
 * the create/update/replace mutations, and the right-side editor composition.
 * The sticky details card lives in `doc-form/DocDetailsForm.tsx`, the
 * upload-mode panel in `AttachmentPanel.tsx`, and the form state + the pure
 * `composePayload` payload builder in `doc-form/formState.ts`.
 *
 * Type-aware editors: when `doc_type === "video"` we render
 * `VideoFieldsEditor` and persist `{ video: {...} }` to the version's
 * metadata; when `doc_type === "faq"` we render a structured Q&A editor
 * that serialises to canonical markdown (so the Rails-side
 * `Documents::FaqParser` round-trips losslessly).
 */
export function DocumentManualForm({
  existing,
}: {
  existing?: DocDetail;
}): JSX.Element {
  const isEdit = !!existing;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initial: FormState = existing
    ? {
        title: existing.title,
        status: existing.status,
        doc_type: existing.doc_type ?? "article",
        document_category_id: existing.category ? String(existing.category.id) : "",
        solution_area_ids: (existing.solution_areas ?? []).map((a) => a.id),
        default_release_id: existing.default_release
          ? String(existing.default_release.id)
          : "",
        content: existing.content ?? "",
        // Hydrate the access panel from what the document already carries.
        // Falling back to `general` matters: a serializer that predates the
        // `access` block must not read as "narrow this document".
        access_scope: existing.access?.scope ?? "general",
        product_codes: (existing.access?.products ?? []).map((p) => p.code),
        feature_codes: (existing.access?.features ?? []).map((f) => f.slug),
        release_ids: (existing.access?.releases ?? []).map((r) => r.id),
      }
    : EMPTY;

  const [form, setForm] = useState<FormState>(initial);
  const [video, setVideo] = useState<VideoFields>(() =>
    existing && (existing.doc_type ?? "article") === "video"
      ? videoFromMetadata(existing.metadata, existing.external_url, existing.thumbnail_url)
      : EMPTY_VIDEO_FIELDS,
  );
  // FAQ rows + a one-way escape hatch into raw-markdown mode for power
  // users editing a doc whose body doesn't fit the canonical Q&A form.
  const initialFaq = useMemo(
    () => parseFaqMarkdown(initial.content) ?? EMPTY_FAQ,
    [initial.content],
  );
  const [faqRaw, setFaqRaw] = useState<boolean>(() => {
    if (!existing) return false;
    if ((existing.doc_type ?? "article") !== "faq") return false;
    return parseFaqMarkdown(existing.content ?? "") === null && (existing.content ?? "").trim().length > 0;
  });
  const [faq, setFaq] = useState<FaqPair[]>(initialFaq);

  const [error, setError] = useState<string | null>(null);
  // Pending attachment removal — drives <ConfirmDialog>. Replaces native confirm().
  const [pendingRemoveAttachment, setPendingRemoveAttachment] = useState<{
    id: number;
    filename: string;
  } | null>(null);

  const { data: cats } = useQuery({
    queryKey: ["docs", "categories"],
    queryFn: listCategories,
  });
  const { data: rels } = useQuery({
    queryKey: ["docs", "releases"],
    queryFn: listReleases,
  });
  const { data: sols } = useQuery({
    queryKey: ["docs", "solution-areas"],
    queryFn: listSolutionAreas,
  });
  const { data: atts } = useQuery({
    queryKey: ["docs", "attachments", existing?.id],
    queryFn: () => listAttachments(existing?.slug ?? ""),
    enabled: isEdit && !!existing?.slug,
  });

  const isVideo = form.doc_type === "video";
  const isFaq   = form.doc_type === "faq";
  const hasContent = form.content.trim().length > 0;
  // The plain markdown editor shows for non-video, non-faq docs (or
  // when the FAQ user has opted into raw-markdown mode). Video docs
  // do not get a markdown editor — the file / external URL IS the
  // content; FAQ docs use the structured editor by default.
  //
  // A doc is "upload-mode" (→ the attachment panel) only when it actually
  // carries a FILE *and* has no markdown body — NOT merely an empty body.
  // Keying off an empty body alone was the "AI generated documents show no
  // file" bug: a freshly AI-generated (or empty-draft) markdown article
  // landed on the edit page rendering "No file attached" instead of the
  // editor. `existing.files` comes from the initial detail, so the decision
  // is stable on first render — no flicker waiting on the attachments query.
  // (Fixed 2026-06-06.)
  const hasFiles = (existing?.files.length ?? 0) > 0;
  const isUploadDoc = isEdit && hasFiles && (existing?.content ?? "").trim().length === 0;
  const showEditor = !isVideo && (!isFaq || faqRaw) && !isUploadDoc;
  const attachments: DocAttachment[] = atts?.data ?? [];

  const replaceMut = useMutation({
    mutationFn: (file: File) => replaceFile(existing!.slug, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["docs"] });
    },
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : "Could not replace file"),
  });

  const removeAttMut = useMutation({
    mutationFn: (attId: number) => deleteAttachment(existing!.slug, attId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["docs"] });
    },
  });

  const save = useMutation({
    mutationFn: async (values: FormState) => {
      const payload = composePayload(values, { video, faqRaw, faq });

      if (isEdit && existing) {
        await updateDoc(existing.slug, {
          document: {
            title: payload.document.title,
            status: payload.document.status,
            doc_type: payload.document.doc_type,
            document_category_id: payload.document.document_category_id,
            default_release_id: payload.document.default_release_id,
            // external_url / thumbnail_url are only present on the video branch;
            // a conditional spread keeps `undefined` off the optional field
            // (exactOptionalPropertyTypes).
            ...(payload.document.external_url !== undefined ? { external_url: payload.document.external_url } : {}),
            ...(payload.document.thumbnail_url !== undefined ? { thumbnail_url: payload.document.thumbnail_url } : {}),
          },
          ...(payload.metadata ? { metadata: payload.metadata } : {}),
          // Many-to-many: top-level, reconciled by the portal-api controller.
          solution_area_ids: payload.solution_area_ids,
        });
        if (payload.content !== (existing.content ?? "")) {
          await bumpVersion(existing.slug, payload.content);
        }
        if (values.status === "published" && existing.status !== "published") {
          await publishDoc(existing.slug);
        }
        return existing.slug;
      }

      const created = await createDoc({
        document: {
          title: payload.document.title,
          status: payload.document.status,
          doc_type: payload.document.doc_type,
          document_category_id: payload.document.document_category_id,
          default_release_id: payload.document.default_release_id,
          ...(payload.document.external_url !== undefined ? { external_url: payload.document.external_url } : {}),
          ...(payload.document.thumbnail_url !== undefined ? { thumbnail_url: payload.document.thumbnail_url } : {}),
        },
        content: payload.content,
        ...(payload.metadata ? { metadata: payload.metadata } : {}),
        solution_area_ids: payload.solution_area_ids,
      });
      if (values.status === "published") {
        await publishDoc(created.data.slug);
      }
      return created.data.slug;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["docs"] });
      navigate("/documentation");
    },
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : "Could not save document"),
  });

  // Reset error when the user starts editing again.
  useEffect(() => {
    if (error) setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title, form.content, form.status, form.doc_type]);

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (form.title.trim().length < 2) {
      setError("Title must be at least 2 characters.");
      return;
    }
    if (isVideo && video.kind !== "upload" && !video.external_url.trim()) {
      setError("Enter the video URL or switch the source to Upload file.");
      return;
    }
    save.mutate(form);
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={submit}
        sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { lg: "minmax(0,420px) minmax(0,1fr)" } }}
      >
        <DocDetailsForm
          form={form}
          setForm={setForm}
          categories={cats?.data ?? []}
          solutionAreas={sols?.data ?? []}
          releases={rels?.data ?? []}
          isEdit={isEdit}
          existing={existing}
          error={error}
          saving={save.isPending}
        >
          <DocAccessPanel
            form={form}
            setForm={setForm}
            releases={rels?.data ?? []}
            {...(existing?.access ? { initialAccess: existing.access } : {})}
          />
        </DocDetailsForm>

        <Box sx={{ display: "grid", minWidth: 0, gap: 2 }}>
          {/* Type-specific editors */}
          {isVideo && <VideoFieldsEditor value={video} onChange={setVideo} />}

          {isFaq && !faqRaw && (
            <>
              <FaqFieldsEditor value={faq} onChange={setFaq} />
              <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
                Need free-form Markdown instead?{" "}
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    setForm((s) => ({ ...s, content: serializeFaqMarkdown(faq) }));
                    setFaqRaw(true);
                  }}
                  sx={{ border: 0, bgcolor: "transparent", p: 0, font: "inherit", color: "inherit", cursor: "pointer", textDecoration: "underline", "&:hover": { color: "text.primary" } }}
                >
                  Switch to raw markdown
                </Box>
                .
              </Typography>
            </>
          )}

          {/* Upload-mode video shows the attachment panel so the staff
              user can pick the MP4 once the doc exists. Mirrors the
              existing upload-doc UX, scoped to video. */}
          {isVideo && isEdit && (
            <>
              <Box
                component="input"
                ref={fileInputRef}
                type="file"
                accept="video/*"
                sx={{ display: "none" }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const f = e.target.files?.[0];
                  if (f) replaceMut.mutate(f);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              />
              <AttachmentPanel
                attachment={attachments[0]}
                {...(existing?.id !== undefined ? { docId: existing.id } : {})}
                onPick={() => fileInputRef.current?.click()}
                onRemove={(id) => {
                  const filename = attachments.find((a) => a.id === id)?.filename ?? "this file";
                  setPendingRemoveAttachment({ id, filename });
                }}
                busy={replaceMut.isPending}
              />
            </>
          )}

          {/* Upload-mode (other doc types) — existing attachment panel.
              Only when the doc truly carries a file (isUploadDoc), never
              for an empty-bodied markdown draft. */}
          {!isVideo && !isFaq && isUploadDoc && (
            <>
              <Box
                component="input"
                ref={fileInputRef}
                type="file"
                sx={{ display: "none" }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const f = e.target.files?.[0];
                  if (f) replaceMut.mutate(f);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              />
              <AttachmentPanel
                attachment={attachments[0]}
                {...(existing?.id !== undefined ? { docId: existing.id } : {})}
                onPick={() => fileInputRef.current?.click()}
                onRemove={(id) => {
                  const filename = attachments.find((a) => a.id === id)?.filename ?? "this file";
                  setPendingRemoveAttachment({ id, filename });
                }}
                busy={replaceMut.isPending}
              />
            </>
          )}

          {(showEditor || isVideo) && (
            <Card>
              <CardContent sx={{ display: "grid", gap: 1 }}>
                <MarkdownEditor
                  id="doc-content"
                  label={
                    isVideo
                      ? "Description (Markdown — optional)"
                      : isFaq && faqRaw
                        ? "Body (raw Markdown)"
                        : "Content (Markdown)"
                  }
                  rows={isVideo ? 10 : 26}
                  placeholder={
                    isVideo
                      ? "Optional: a few paragraphs that appear below the player."
                      : "# Title\n\nBody of the document in Markdown…"
                  }
                  value={form.content}
                  onChange={(next) => setForm((s) => ({ ...s, content: next }))}
                  helperText={
                    <>
                      {!isVideo && !isFaq && (
                        <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
                          A new version is recorded automatically each time the body
                          changes. {hasContent ? "" : "Empty bodies are allowed for drafts."}
                        </Typography>
                      )}
                      {isFaq && faqRaw && (
                        <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
                          Free-form mode. The portal still parses H2 headings into
                          Q&amp;A accordion items;{" "}
                          <Box
                            component="button"
                            type="button"
                            onClick={() => {
                              const parsed = parseFaqMarkdown(form.content);
                              setFaq(parsed ?? EMPTY_FAQ);
                              setFaqRaw(false);
                            }}
                            sx={{ border: 0, bgcolor: "transparent", p: 0, font: "inherit", color: "inherit", cursor: "pointer", textDecoration: "underline", "&:hover": { color: "text.primary" } }}
                          >
                            return to the structured editor
                          </Box>
                          .
                        </Typography>
                      )}
                    </>
                  }
                />
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
      <ConfirmDialog
        isOpen={pendingRemoveAttachment !== null}
        onClose={() => setPendingRemoveAttachment(null)}
        title="Remove attachment?"
        description={
          pendingRemoveAttachment
            ? `"${pendingRemoveAttachment.filename}" will be removed from this document.`
            : ""
        }
        confirmLabel="Remove file"
        onConfirm={() => {
          if (!pendingRemoveAttachment) return;
          removeAttMut.mutate(pendingRemoveAttachment.id);
        }}
      />
    </>
  );
}
